import { ArrowRight, FlaskConical, House, MonitorUp, Search, Sparkles, X } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type { WorldKind } from '@core/types';
import { EntryGate } from './components/EntryGate';
import { LandingPage } from './pages/LandingPage';
import { routeTo } from './lib';

const BlueprintPage = lazy(() => import('./pages/BlueprintPage').then((module) => ({ default: module.BlueprintPage })));
const CompanionPage = lazy(() => import('./pages/CompanionPage').then((module) => ({ default: module.CompanionPage })));
const DownloadPage = lazy(() => import('./pages/DownloadPage').then((module) => ({ default: module.DownloadPage })));
const LabPage = lazy(() => import('./pages/LabPage').then((module) => ({ default: module.LabPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((module) => ({ default: module.NotFoundPage })));
const OverlayPage = lazy(() => import('./pages/OverlayPage').then((module) => ({ default: module.OverlayPage })));
const StagePage = lazy(() => import('./pages/StagePage').then((module) => ({ default: module.StagePage })));
const TraineePage = lazy(() => import('./pages/TraineePage').then((module) => ({ default: module.TraineePage })));

function detectWorld(): WorldKind {
  const requested = new URLSearchParams(window.location.search).get('world');
  if (requested === 'black' || requested === 'pink') return requested;
  return window.location.hostname.includes('black') ? 'black' : 'pink';
}

const commands = [
  { path: '/', label: 'Home / Entry', icon: House },
  { path: '/stage', label: 'Trainee Stage', icon: Sparkles },
  { path: '/companion', label: 'Live Habitat', icon: MonitorUp },
  { path: '/lab', label: 'Soul Lab', icon: FlaskConical },
  { path: '/blueprint', label: 'System Blueprint', icon: ArrowRight },
];

export default function App(): JSX.Element {
  const [route, setRoute] = useState(() => `${window.location.pathname}${window.location.search}`);
  const [palette, setPalette] = useState(false);
  const world = useMemo(detectWorld, [route]);
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const gateRequested = path === '/entry' || new URLSearchParams(window.location.search).has('gate');
  const [entered, setEntered] = useState(() => !gateRequested || sessionStorage.getItem('memes.entry') === '1' || new URLSearchParams(window.location.search).has('skip'));

  useEffect(() => {
    const onRoute = () => setRoute(`${window.location.pathname}${window.location.search}`);
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setPalette((value) => !value); }
      if (event.key === 'Escape') setPalette(false);
    };
    window.addEventListener('popstate', onRoute);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('popstate', onRoute); window.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.world = world;
    document.title = world === 'pink' ? 'MEMES.PINK — Companion Species' : 'MEMES.BLACK — Soul Laboratory';
  }, [world]);

  let page: JSX.Element;
  if (path === '/' || path === '/entry') page = <LandingPage world={world} />;
  else if (path === '/stage') page = <StagePage world={world} />;
  else if (path.startsWith('/trainee/')) page = <TraineePage world={world} id={decodeURIComponent(path.slice('/trainee/'.length))} />;
  else if (path === '/companion') page = <CompanionPage key={route} world={world} />;
  else if (path === '/lab') page = <LabPage world={world} />;
  else if (path === '/blueprint') page = <BlueprintPage world={world} />;
  else if (path === '/download') page = <DownloadPage world={world} />;
  else if (path === '/overlay') page = <OverlayPage />;
  else page = <NotFoundPage world={world} />;

  return <>
    {gateRequested && !entered ? <EntryGate world={world} onEnter={() => { sessionStorage.setItem('memes.entry', '1'); setEntered(true); routeTo('/'); }} /> : <Suspense fallback={<div className="route-loader"><i /><span>FORMING BODY</span></div>}>{page}</Suspense>}
    {palette ? <div className="command-palette-backdrop" onMouseDown={() => setPalette(false)} role="presentation"><div className="command-palette" onMouseDown={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command menu"><div className="command-input"><Search size={16} /><span>GO TO…</span><button onClick={() => setPalette(false)}><X size={15} /></button></div>{commands.map(({ path: target, label, icon: Icon }) => <button key={target} onClick={() => { routeTo(target); setPalette(false); }}><Icon size={15} /><span>{label}</span><small>↵</small></button>)}</div></div> : null}
  </>;
}
